// src/context/AdminCommerceContext.tsx
import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
    useMemo,
    useCallback,
    useRef,
} from "react";

import { adminCommerceService } from "../api/service/admin/commerce.service";
import { adminOrdersService } from "../api/service/admin/admin_orders.service";
import {
    AdminDashboardData,
    AdminTransaction,
    AdminCustomer,
    TopProduct,
    CategoryStats,
    ProductStatsOverview,
    SalesReportItem,
    ReportOrdersData,
    AdminOrder,
} from "../api/interface/admin.interface";

// ===============================
// TYPES
// ===============================
interface AdminCommerceContextType {
    // Dashboard
    dashboardData: AdminDashboardData | null;
    isLoadingDashboard: boolean;
    refreshDashboard: () => Promise<void>;

    // Product stats
    productStatsOverview: ProductStatsOverview | null;
    topProducts: TopProduct[] | null;
    categoryStats: CategoryStats[] | null;
    isLoadingProductStats: boolean;
    refreshProductStats: (period?: 'month' | 'year' | 'all') => Promise<void>;

    // Customers
    customers: AdminCustomer[] | null;
    isLoadingCustomers: boolean;
    refreshCustomers: () => Promise<void>;

    // Transactions
    transactions: AdminTransaction[] | null;
    isLoadingTransactions: boolean;
    refreshTransactions: (filters?: any) => Promise<void>;

    // Orders
    orders: AdminOrder[] | null;
    isLoadingOrders: boolean;
    refreshOrders: (filters?: any) => Promise<void>;

    // Reports
    ordersReport: ReportOrdersData | null;
    salesReport: SalesReportItem[] | null;
    isLoadingReports: boolean;
    refreshOrdersReport: (filters?: any) => Promise<void>;
    refreshSalesReport: (filters?: any) => Promise<void>;

    // Exports
    exportCsv: (params: { type: 'orders' | 'products'; date_from?: string; date_to?: string }) => Promise<Blob>;
    exportPdf: (params: { type?: 'orders' | 'products'; date_from?: string; date_to?: string }) => Promise<Blob>;
}

// ===============================
// CONTEXT
// ===============================
const AdminCommerceContext = createContext<AdminCommerceContextType | undefined>(undefined);

// ===============================
// PROVIDER
// ===============================
export const AdminCommerceProvider = ({ children }: { children: ReactNode }) => {
    // ===============================
    // STATES
    // ===============================
    // Dashboard
    const [dashboardData, setDashboardData] = useState<AdminDashboardData | null>(null);
    const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
    const fetchingDashboardRef = useRef(false);

    // Product stats
    const [productStatsOverview, setProductStatsOverview] = useState<ProductStatsOverview | null>(null);
    const [topProducts, setTopProducts] = useState<TopProduct[] | null>(null);
    const [categoryStats, setCategoryStats] = useState<CategoryStats[] | null>(null);
    const [isLoadingProductStats, setIsLoadingProductStats] = useState(false);
    const fetchingProductStatsRef = useRef(false);

    // Customers
    const [customers, setCustomers] = useState<AdminCustomer[] | null>(null);
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const fetchingCustomersRef = useRef(false);

    // Transactions
    const [transactions, setTransactions] = useState<AdminTransaction[] | null>(null);
    const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
    const fetchingTransactionsRef = useRef(false);

    // Orders
    const [orders, setOrders] = useState<AdminOrder[] | null>(null);
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);
    const fetchingOrdersRef = useRef(false);

    // Reports
    const [ordersReport, setOrdersReport] = useState<ReportOrdersData | null>(null);
    const [salesReport, setSalesReport] = useState<SalesReportItem[] | null>(null);
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const fetchingOrdersReportRef = useRef(false);
    const fetchingSalesReportRef = useRef(false);

    // ===============================
    // LOADERS
    // ===============================
    const loadDashboard = useCallback(async () => {
        if (fetchingDashboardRef.current) return;
        fetchingDashboardRef.current = true;
        setIsLoadingDashboard(true);
        try {
            const res = await adminCommerceService.getDashboard();
            if (res.success) {
                setDashboardData(res.data);
            } else {
                setDashboardData(null);
            }
        } catch {
            setDashboardData(null);
        } finally {
            fetchingDashboardRef.current = false;
            setIsLoadingDashboard(false);
        }
    }, []);

    const loadProductStats = useCallback(async (period?: 'month' | 'year' | 'all') => {
        if (fetchingProductStatsRef.current) return;
        fetchingProductStatsRef.current = true;
        setIsLoadingProductStats(true);
        try {
            const [overview, top, categories] = await Promise.all([
                adminCommerceService.getProductStatsOverview(period),
                adminCommerceService.getTopProducts(10, period),
                adminCommerceService.getCategoriesStats(period),
            ]);
            if (overview.success) setProductStatsOverview(overview.data);
            if (top.success) setTopProducts(top.data);
            if (categories.success) setCategoryStats(categories.data);
        } catch {
            setProductStatsOverview(null);
            setTopProducts(null);
            setCategoryStats(null);
        } finally {
            fetchingProductStatsRef.current = false;
            setIsLoadingProductStats(false);
        }
    }, []);

    const loadCustomers = useCallback(async () => {
        if (fetchingCustomersRef.current) return;
        fetchingCustomersRef.current = true;
        setIsLoadingCustomers(true);
        try {
            const res = await adminCommerceService.getCustomers();
            if (res.success) {
                setCustomers(res.data.data);
            } else {
                setCustomers(null);
            }
        } catch {
            setCustomers(null);
        } finally {
            fetchingCustomersRef.current = false;
            setIsLoadingCustomers(false);
        }
    }, []);

    const loadTransactions = useCallback(async (filters?: any) => {
        if (fetchingTransactionsRef.current) return;
        fetchingTransactionsRef.current = true;
        setIsLoadingTransactions(true);
        try {
            const cleanFilters = { ...filters };
            // Supprimer les filtres vides ou 'all'
            if (cleanFilters.status === 'all') {
                delete cleanFilters.status;
            }
            const res = await adminCommerceService.getTransactions(cleanFilters);
            if (res.success) {
                setTransactions(res.data.data);
            } else {
                setTransactions(null);
            }
        } catch {
            setTransactions(null);
        } finally {
            fetchingTransactionsRef.current = false;
            setIsLoadingTransactions(false);
        }
    }, []);
    
    const loadOrders = useCallback(async (filters?: any) => {
        if (fetchingOrdersRef.current) return;
        fetchingOrdersRef.current = true;
        setIsLoadingOrders(true);
        try {
            const res = await adminOrdersService.getOrders(filters);
            if (res.success) {
                setOrders(res.data.data);
            } else {
                setOrders(null);
            }
        } catch {
            setOrders(null);
        } finally {
            fetchingOrdersRef.current = false;
            setIsLoadingOrders(false);
        }
    }, []);

    const loadOrdersReport = useCallback(async (filters?: any) => {
        if (fetchingOrdersReportRef.current) return;
        fetchingOrdersReportRef.current = true;
        setIsLoadingReports(true);
        try {
            const res = await adminCommerceService.getOrdersReport(filters);
            if (res.success) {
                setOrdersReport(res.data);
            } else {
                setOrdersReport(null);
            }
        } catch {
            setOrdersReport(null);
        } finally {
            fetchingOrdersReportRef.current = false;
            setIsLoadingReports(false);
        }
    }, []);

    const loadSalesReport = useCallback(async (filters?: any) => {
        if (fetchingSalesReportRef.current) return;
        fetchingSalesReportRef.current = true;
        setIsLoadingReports(true);
        try {
            const res = await adminCommerceService.getSalesReport(filters);
            if (res.success) {
                setSalesReport(res.data);
            } else {
                setSalesReport(null);
            }
        } catch {
            setSalesReport(null);
        } finally {
            fetchingSalesReportRef.current = false;
            setIsLoadingReports(false);
        }
    }, []);

    // ===============================
    // REFRESH METHODS
    // ===============================
    const refreshDashboard = useCallback(async () => { await loadDashboard(); }, [loadDashboard]);
    const refreshProductStats = useCallback(async (period?: 'month' | 'year' | 'all') => {
        await loadProductStats(period);
    }, [loadProductStats]);
    const refreshCustomers = useCallback(async () => { await loadCustomers(); }, [loadCustomers]);
    const refreshTransactions = useCallback(async (filters?: any) => {
        await loadTransactions(filters);
    }, [loadTransactions]);
    const refreshOrders = useCallback(async (filters?: any) => {
        await loadOrders(filters);
    }, [loadOrders]);
    const refreshOrdersReport = useCallback(async (filters?: any) => {
        await loadOrdersReport(filters);
    }, [loadOrdersReport]);
    const refreshSalesReport = useCallback(async (filters?: any) => {
        await loadSalesReport(filters);
    }, [loadSalesReport]);

    // ===============================
    // EXPORTS (direct calls)
    // ===============================
    const exportCsv = useCallback(async (params: { type: 'orders' | 'products'; date_from?: string; date_to?: string }) => {
        return await adminCommerceService.exportCsv(params);
    }, []);

    const exportPdf = useCallback(async (params: { type?: 'orders' | 'products'; date_from?: string; date_to?: string }) => {
        return await adminCommerceService.exportPdf(params);
    }, []);

    // ===============================
    // INIT
    // ===============================
    useEffect(() => {
        loadDashboard();
        loadProductStats('all');
        loadCustomers();
        loadTransactions();
        loadOrders();
        loadOrdersReport();
        loadSalesReport();
    }, []);

    // ===============================
    // VALUE
    // ===============================
    const value = useMemo(
        () => ({
            dashboardData,
            isLoadingDashboard,
            refreshDashboard,

            productStatsOverview,
            topProducts,
            categoryStats,
            isLoadingProductStats,
            refreshProductStats,

            customers,
            isLoadingCustomers,
            refreshCustomers,

            transactions,
            isLoadingTransactions,
            refreshTransactions,

            orders,
            isLoadingOrders,
            refreshOrders,

            ordersReport,
            salesReport,
            isLoadingReports,
            refreshOrdersReport,
            refreshSalesReport,

            exportCsv,
            exportPdf,
        }),
        [
            dashboardData,
            isLoadingDashboard,
            refreshDashboard,
            productStatsOverview,
            topProducts,
            categoryStats,
            isLoadingProductStats,
            refreshProductStats,
            customers,
            isLoadingCustomers,
            refreshCustomers,
            transactions,
            isLoadingTransactions,
            refreshTransactions,
            orders,
            isLoadingOrders,
            refreshOrders,
            ordersReport,
            salesReport,
            isLoadingReports,
            refreshOrdersReport,
            refreshSalesReport,
            exportCsv,
            exportPdf,
        ]
    );

    return (
        <AdminCommerceContext.Provider value={value}>
            {children}
        </AdminCommerceContext.Provider>
    );
};

// ===============================
// HOOK
// ===============================
export const useAdminCommerce = () => {
    const context = useContext(AdminCommerceContext);
    if (!context) {
        throw new Error("useAdminCommerce doit être utilisé dans AdminCommerceProvider");
    }
    return context;
};